// The effect grants and the effect picks, as the MODDING panel changes them
// (effects.md §3, R908, R909).
//
// This is the one module that writes an effect pick. It holds the last
// answer of `GET /api/effects` and answers every reader from it, so a panel
// that renders four times a second reads no promise and counts no state of
// its own.
//
// The rules it holds:
//   - **The pick is local first.** `setPick` writes the pick into the held
//     answer and then sends the WHOLE map, because the route replaces the
//     stored picks. A refusal puts the map back as it was and throws, and
//     the panel says what the server said. It then re-reads the stored
//     picks, because the map it put back can hold an earlier pick that the
//     server refused as well (R1345).
//   - **A grant is the gate.** `isGranted` answers from the held answer
//     alone. A code with no grant stores nothing: the panel shows the
//     LOCKED tag and how a player gets the effect.
//   - **Nothing here reaches the game.** A pick rides the next join ticket
//     (R909), so a change shows in the world after the next join.

import { getEffects, putEffectPicks, type AccountEffects, type EffectGrant } from '../net/api';

/** The last answer of either effect route, or null before the first read. */
let held: AccountEffects | null = null;

/** How many writes started. A reader presses an arrow faster than a route
 *  answers, so an answer takes the picks only while its write is the last
 *  one: an older answer must never put a newer pick back. */
let writes = 0;

/** The last answer the account's effect routes gave. */
export function lastEffects(): AccountEffects | null {
  return held;
}

/** Holds one answer of either effect route. Every reader below uses it. */
function rememberEffects(a: AccountEffects): AccountEffects {
  held = a;
  return a;
}

/** Reads the account's grants and picks, and holds the answer. The MODDING
 *  panel loads its cache cell through this. */
export async function loadEffects(): Promise<AccountEffects> {
  return rememberEffects(await getEffects());
}

/** The grant the account holds for one effect code, or null. */
export function grantOf(code: string): EffectGrant | null {
  return held?.granted.find((g) => g.code === code) ?? null;
}

/** Whether the account holds a grant of that effect code. */
export function isGranted(code: string): boolean {
  return grantOf(code) !== null;
}

/** The effect code one slot wears, or '' where the slot wears none. */
export function pickOf(slot: string): string {
  return held?.picks[slot] ?? '';
}

/**
 * Writes one slot's pick and sends every pick the account holds.
 *
 * `code` is an effect code, or '' to clear the slot. The held answer takes
 * the pick at once, so the row shows it before the answer lands. A refusal
 * puts the held picks back and throws what the route threw. A call before
 * the first read holds no picks to send, so it writes nothing.
 */
export async function setPick(slot: string, code: string): Promise<void> {
  const a = held;
  if (!a) return;
  const before = a.picks;
  const next: Record<string, string> = { ...before };
  if (code === '') delete next[slot];
  else next[slot] = code;
  a.picks = next;
  const mine = ++writes;
  try {
    const answer = await putEffectPicks(next);
    // A later write is already in flight, so its picks are the newer truth.
    // The rest of the answer — the grants and the slots — is still current.
    rememberEffects(mine === writes ? answer : { ...answer, picks: held?.picks ?? answer.picks });
  } catch (err) {
    // The server refused, so the stored picks did not change.
    if (mine === writes) a.picks = before;
    // `before` can hold an earlier pick still in flight, and that pick can
    // be refused too. The stored picks are the truth, so read them (R1345).
    void rereadAfterRefusal();
    throw err;
  }
}

/** Reads the stored picks after a refusal. The answer is held only while
 *  no newer write started, so it never puts a newer pick back (R1345). */
async function rereadAfterRefusal(): Promise<void> {
  const at = writes;
  try {
    const answer = await getEffects();
    if (at === writes) rememberEffects(answer);
  } catch {
    // The refusal already told the panel. The next load reads again.
  }
}

/** Drops the held answer. The tests and a sign-out call it. */
export function resetEffectState(): void {
  held = null;
  writes = 0;
}
